<template>
    <div class="score-info">
        <div v-if="currentView === 'main'" class="score-info-modal">
            <div class="information pr-2">
                <div class="info-title mb-4">{{ $translate('score_info_title') }}</div>

                <div class="info-text" v-html="scoreInfoText"></div>

                <b-button variant="link" class="learn-more" @click="setView('confidence')">
                    <span>{{ $translate('learn_about_our_score_confidence') }}</span>
                </b-button>
            </div>

            <div class="eq-score">
                <span class="score-title">{{ $translate('implantation') }}</span>

                <vue-apex-charts
                    type="radialBar"
                    height="200"
                    width="200"
                    :options="kidChartOptions"
                    :series="kidChartSeries"
                />

                <div
                    v-b-tooltip.hover
                    class="line-chart-holder"
                    :title="
                        $translate('embryo_was_better_than_x_of_implanted', kidFPRangeMetric.value)
                    "
                >
                    <range-metric :range-metric="kidFPRangeMetric" metric-title="your_embryo" />
                </div>

                <div
                    v-b-tooltip.hover
                    class="line-chart-holder"
                    :title="
                        $translate(
                            'in_our_test_dataset_embryos_that_got_this_had_x_to_implant',
                            kidPRangeMetric.value
                        )
                    "
                >
                    <range-metric :range-metric="kidPRangeMetric" />
                </div>
            </div>

            <div class="blast-score">
                <span class="score-title">{{ $translate('blast') }}</span>

                <span v-if="well.isBlast" class="score-info-checkmark">
                    <i class="fa fa-check" />
                </span>

                <vue-apex-charts
                    type="radialBar"
                    :options="blastChartOptions"
                    height="200"
                    width="200"
                    :series="blastChartSeries"
                />

                <div
                    v-b-tooltip.hover
                    class="line-chart-holder"
                    :title="
                        $translate('embryo_was_better_than_x_of_blasts', blastFPRangeMetric.value)
                    "
                >
                    <range-metric :range-metric="blastFPRangeMetric" metric-title="your_embryo" />
                </div>
            </div>
        </div>

        <div v-else-if="currentView === 'confidence'" class="confidence-modal">
            <div class="header">
                <b-button variant="transparent" class="mr-3" @click="setView('main')">
                    <i class="fa fa-chevron-left" />
                </b-button>

                <span class="score-explained">{{ $translate('score_explained') }}</span>
            </div>

            <div class="comment" v-html="$translate('score_info_confidence_text')"></div>

            <bar-chart
                class="chart-wrapper"
                :styles="scoreConfidenceChartStyles"
                :chartdata="scoreConfidenceChartData"
            />

            <div class="scale">
                <span>0</span>
                <span v-for="num in 10" :key="num">{{ num }}</span>
            </div>

            <div class="score-explained-chart-x-axis-text">
                <span v-html="$translate('more_confident_no_pregnancy_text')"></span>
                <span v-html="$translate('less_confident_no_pregnancy_text')"></span>
                <span v-html="$translate('not_confident_text')"></span>
                <span v-html="$translate('less_confident_yes_pregnancy_text')"></span>
                <span v-html="$translate('more_confident_yes_pregnancy_text')"></span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import BarChart from '../common/BarChart'
import RangeMetric from '../common/RangeMetric'
import UIUtils from '../../../business/utils/UIUtils'
import Theme from '../../../business/utils/Theme'

export default {
    name: 'ScoreInfo',
    components: {
        VueApexCharts,
        BarChart,
        RangeMetric
    },
    props: {
        well: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        currentView: 'main'
    }),
    computed: {
        ...mapState({
            selectedTenant: state => state.common.selectedTenant
        }),
        inferenceData() {
            return this.well.inferenceData
        },
        scoreInfoText() {
            return this.$translate('score_info_text')
        },
        kidChartSeries() {
            return this.generateScoreSeries(this.inferenceData.kidScore)
        },
        blastChartSeries() {
            return this.generateScoreSeries(this.inferenceData.blastScore)
        },
        kidChartOptions() {
            if (!this.selectedTenant._id) return null

            return this.generateScoreChartOptions(
                this.inferenceData.kidScore,
                UIUtils.getKidScoreColor
            )
        },
        blastChartOptions() {
            if (!this.selectedTenant._id) return null

            return this.generateScoreChartOptions(
                this.inferenceData.blastScore,
                UIUtils.getBlastScoreColor
            )
        },
        scoreConfidenceChartStyles() {
            return {
                height: '6rem'
            }
        },
        scoreConfidenceChartData() {
            return {
                labels: [0, 2, 4, 6, 8],
                datasets: [
                    {
                        label: 'Confidence',
                        backgroundColor: [
                            Theme.chartColors.scoreInfo.high,
                            Theme.chartColors.scoreInfo.med,
                            Theme.chartColors.scoreInfo.low,
                            Theme.chartColors.scoreInfo.med,
                            Theme.chartColors.scoreInfo.high
                        ],
                        borderWidth: 1,
                        data: [10, 5, 1, 5, 10]
                    }
                ]
            }
        },
        kidPRangeMetric() {
            const { kidScoreProb } = this.inferenceData.confidence

            return {
                systemName: 'estimated_implantation_probability',
                value: UIUtils.formatPercentage(kidScoreProb),
                min: 0,
                max: 100
            }
        },
        blastFPRangeMetric() {
            const { blastScoreTP } = this.inferenceData.confidence

            return {
                systemName: 'compared_to_implanted_embryos',
                value: UIUtils.formatPercentage(blastScoreTP),
                min: 0,
                max: 100
            }
        },
        kidFPRangeMetric() {
            const { kidScoreTP } = this.inferenceData.confidence

            return {
                systemName: 'compared_to_implanted_embryos',
                value: UIUtils.formatPercentage(kidScoreTP),
                min: 0,
                max: 100
            }
        }
    },
    methods: {
        setView(view) {
            this.currentView = view
        },
        generateScoreChartOptions(score, colorFunction) {
            const color = colorFunction(this.selectedTenant, score, false, false, true)

            return {
                colors: [color],
                plotOptions: {
                    radialBar: {
                        startAngle: -170,
                        endAngle: 190,
                        hollow: {
                            size: '67%',
                            background: 'transparent'
                        },
                        track: {
                            background: Theme.themeColors.dark,
                            strokeWidth: '97%'
                        },
                        dataLabels: {
                            name: {
                                offsetY: -25,
                                color: Theme.themeColors.light,
                                fontSize: '11pt'
                            },
                            value: {
                                offsetY: -5,
                                color,
                                formatter: function (val) {
                                    return (val / 10).toFixed(1)
                                },
                                fontSize: '22pt',
                                fontWeight: 'bold',
                                show: true
                            }
                        }
                    }
                },
                labels: [this.$translate('our_score')]
            }
        },
        generateScoreSeries(score) {
            return [score * 100]
        }
    }
}
</script>

<style lang="scss" scoped>
.score-info {
    .score-info-modal {
        display: flex;

        .information {
            width: 27rem;
            color: $light;
            border-right: solid 0.1rem $dark;

            .info-title {
                font-size: 24px;
                color: $light-3;
            }

            .info-text {
                margin-bottom: 3rem;
            }

            .learn-more {
                padding-left: 0;

                @media screen and #{$new-break2close} {
                    display: none;
                }
            }
        }

        .eq-score,
        .blast-score {
            width: 20.25rem;
            padding: 0 3rem;
            display: flex;
            flex-direction: column;
            align-items: center;

            .score-title {
                color: $light-3;
                font-size: 18px;
                font-weight: 600;
            }
        }

        .eq-score {
            border-right: $border-narrow-dark;
        }

        .blast-score {
            position: relative;

            .score-info-checkmark {
                padding: 0.5rem 1.5rem;
                position: absolute;
                left: calc(50% - 2.25rem);
                top: 7.25rem;
                background-color: $dark-2;
                z-index: 2;

                .fa-check {
                    color: $green;
                    font-size: 28px;
                }
            }
        }

        .line-chart-holder {
            display: flex;
            flex-direction: column;
            padding: 1.5rem 0;
        }

        @media screen and #{$new-break4close} {
            flex-direction: column;

            .eq-score,
            .blast-score {
                width: unset;
            }

            .eq-score {
                border-right: unset;
            }
        }
    }

    .confidence-modal {
        position: relative;
        display: flex;
        flex-direction: column;

        .header {
            display: flex;

            .fa-chevron-left {
                font-size: 20px;
            }

            .score-explained {
                font-size: 24px;
                color: $light-3;
            }
        }

        .comment {
            color: $light;
            margin: 3rem 0;
        }

        .scale {
            display: flex;
            justify-content: space-between;
            color: $light;
            padding: 1rem 0;
        }

        .score-explained-chart-x-axis-text {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            column-gap: 10px;
            color: $light;
            font-size: 13px;

            span {
                padding: 0 10px;
            }
        }
    }
}
</style>

<style lang="scss">
.score-info {
    .confidence-modal {
        @media screen and #{$new-break4close} {
            .chartjs-render-monitor {
                width: calc(197vw - 3rem) !important;
            }
        }
    }
}
</style>
